/**
 * PhotoUpload Component
 * Ortak fotoğraf yükleme komponenti
 * FashPlay ve FashSnap tarafından kullanılır
 * 
 * Selfie (zorunlu) + Boy fotoğrafı (opsiyonel) yükleme/çekme
 */

import React, { useState, useRef } from 'react';
import { StepHeader } from '../StepHeader';
import './PhotoUpload.css';

export interface PhotoUploadResult {
    selfieBase64: string;
    bodyBase64: string | null;
    hasBodyPhoto: boolean;
}

export interface PhotoUploadProps {
    onComplete: (data: PhotoUploadResult) => void;
    title?: string;
    subtitle?: string;
    /** Whether to show the body photo upload card (default: true) */
    showBodyUpload?: boolean;
    initialSelfie?: string | null;
    initialBody?: string | null;
    demoMode?: boolean;
    onSelectDemoPerson?: (personId: string) => void;
}

const DEMO_PROFILES = [
  { id: 'melis', name: 'Melis K.', gender: 'female', age: 30, height: 165, weight: 58, image: '/assets/showcase/selfie_melis.jpg' },
  { id: 'can', name: 'Can Y.', gender: 'male', age: 28, height: 180, weight: 76, image: '/assets/showcase/selfie_can.jpg' },
  { id: 'emre', name: 'Emre A.', gender: 'male', age: 33, height: 185, weight: 82, image: '/assets/showcase/selfie_emre.jpg' }
];

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
    onComplete,
    title = 'Fotoğraflarınızı Yükleyin',
    subtitle = 'Size özel avatar oluşturmak için fotoğraflarınıza ihtiyacımız var.',
    showBodyUpload = true,
    initialSelfie = null,
    initialBody = null,
    demoMode = false,
    onSelectDemoPerson
}) => {
    const [selfie, setSelfie] = useState<string | null>(initialSelfie);
    const [body, setBody] = useState<string | null>(initialBody);
    const [isSkippingBody, setIsSkippingBody] = useState(false);

    React.useEffect(() => {
        setSelfie(initialSelfie);
        if (initialSelfie) {
            setIsSkippingBody(true);
        }
    }, [initialSelfie]);

    React.useEffect(() => {
        setBody(initialBody);
    }, [initialBody]);

    const selfieInputRef = useRef<HTMLInputElement>(null);
    const bodyInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'selfie' | 'body') => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            if (type === 'selfie') {
                setSelfie(result);
            } else {
                setBody(result);
                setIsSkippingBody(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleContinue = () => {
        if (!selfie) return;

        if (!showBodyUpload) {
            // Body upload disabled → treat as skipped
            onComplete({
                selfieBase64: selfie,
                bodyBase64: null,
                hasBodyPhoto: false
            });
            return;
        }

        if (body) {
            onComplete({
                selfieBase64: selfie,
                bodyBase64: body,
                hasBodyPhoto: true
            });
        } else if (isSkippingBody) {
            onComplete({
                selfieBase64: selfie,
                bodyBase64: null,
                hasBodyPhoto: false
            });
        }
    };

    const handleSkipBody = () => {
        setIsSkippingBody(true);
        setBody(null);
    };

    const canContinue = selfie && (body || isSkippingBody || !showBodyUpload);

    return (
        <div className="photo-upload-step">
            <StepHeader title={title} subtitle={subtitle} />

            {demoMode && (
                <div className="demo-profiles-container">
                    <h4 className="demo-profiles-title">Hızlı Deneme için Model Seçin:</h4>
                    <div className="demo-profiles-grid">
                        {DEMO_PROFILES.map(profile => (
                            <button
                                key={profile.id}
                                type="button"
                                className={`demo-profile-card ${selfie === profile.image ? 'active' : ''}`}
                                onClick={() => {
                                    setSelfie(profile.image);
                                    setIsSkippingBody(true);
                                    if (onSelectDemoPerson) {
                                        onSelectDemoPerson(profile.id);
                                    }
                                }}
                            >
                                <img src={profile.image} alt={profile.name} className="demo-profile-avatar" />
                                <span className="demo-profile-name">{profile.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className={`photo-upload-step__grid ${!showBodyUpload ? 'single' : ''}`}>
                {/* Selfie Upload - Mandatory */}
                <div 
                    className={`photo-upload-card ${selfie ? 'filled' : ''}`}
                    onClick={() => selfieInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={selfieInputRef}
                        onChange={(e) => handleFileChange(e, 'selfie')}
                        accept="image/*"
                        hidden
                    />
                    {selfie ? (
                        <div className="photo-preview">
                            <img src={selfie} alt="Selfie" />
                            <div className="photo-status">✓ Yüklendi</div>
                        </div>
                    ) : (
                        <div className="upload-placeholder">
                            <span className="icon">📸</span>
                            <span className="label">Yüz Fotoğrafı (Zorunlu)</span>
                            <span className="sub-label">Selfie çekin veya yükleyin</span>
                        </div>
                    )}
                </div>

                {/* Body Upload - Optional */}
                {showBodyUpload && (
                    <div 
                        className={`photo-upload-card ${body ? 'filled' : ''} ${isSkippingBody ? 'skipped' : ''}`}
                        onClick={() => !isSkippingBody && bodyInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            ref={bodyInputRef}
                            onChange={(e) => handleFileChange(e, 'body')}
                            accept="image/*"
                            hidden
                        />
                        {body ? (
                            <div className="photo-preview">
                                <img src={body} alt="Body" />
                                <div className="photo-status">✓ Yüklendi</div>
                                <button 
                                    className="remove-photo-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setBody(null);
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        ) : isSkippingBody ? (
                            <div className="skipped-placeholder">
                                <span className="icon">🚫</span>
                                <span className="label">Boy Fotoğrafı Atlandı</span>
                                <button 
                                    className="undo-skip-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsSkippingBody(false);
                                    }}
                                >
                                    Fotoğraf Yükle
                                </button>
                            </div>
                        ) : (
                            <div className="upload-placeholder">
                                <span className="icon">🕴️</span>
                                <span className="label">Boy Fotoğrafı (İsteğe Bağlı)</span>
                                <span className="sub-label">Daha gerçekçi sonuç için</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showBodyUpload && !body && !isSkippingBody && (
                <div className="skip-section">
                    <button 
                        type="button"
                        className="text-btn skip-body-btn"
                        onClick={handleSkipBody}
                    >
                        <span className="skip-body-btn__icon" aria-hidden>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M8 14c1.2 1.5 2.8 2.25 4 2.25s2.8-.75 4-2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M9 9.5h.01M15 9.5h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <ellipse cx="7" cy="11" rx="1.8" ry="1.1" fill="currentColor" opacity="0.25" />
                                <ellipse cx="17" cy="11" rx="1.8" ry="1.1" fill="currentColor" opacity="0.25" />
                            </svg>
                        </span>
                        <span className="skip-body-btn__label">
                            Henüz hazır değil misin? Sorun değil — bu adımı buraya tıklayarak atlayabilirsin.
                        </span>
                    </button>
                </div>
            )}

            <div className="photo-upload-actions">
                <button
                    className="primary-btn continue-btn"
                    onClick={handleContinue}
                    disabled={!canContinue}
                >
                    Devam Et
                </button>
            </div>
        </div>
    );
};

export default PhotoUpload;
